/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {FormikErrors, useFormik} from 'formik'
import {Dispatch} from 'react'
import {useIntl} from 'react-intl'
import { Eye, EyeOff } from 'lucide-react'
import {useSetNewPassword} from '../../../../hooks/queries'

const initialValues = {
  new_password: '',
  confirmPassword: '',
}
interface fetchLoginValueProps {
  new_password: string
  confirmPassword: string
}

export function SetNewPassword () {
  const [isError, setIsError] = useState(false)
  const [pass, setPass] = useState(false)
  const [confirmPass, setConfirmPass] = useState(false)
  const [new_password, setNew_password] = useState('')
  const params = useParams()
  const {uid, token} = params
  const navigate = useNavigate()
  const intl = useIntl()

  // Use React Query set new password mutation
  const setNewPasswordMutation = useSetNewPassword()
  /**
   * Defines the validation schema for the login form.
   */
  const loginSchema = Yup.object().shape({
    new_password: Yup.string()
      .min(10, intl.formatMessage({id: 'Minimum 10 symbols'}))
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('new_password'), ''], 'Passwords must match')
      .required('Confirm Password is required'),
  })

  /**
   * Sets a new password using React Query mutation
   *
   * @param values - An object containing the new password.
   * @param setStatus - A function to set the status message.
   * @param setSubmitting - A function to set the submitting state of the form.
   * @param setErrors - A function to set form errors.
   * @returns A Promise that resolves when the password reset is complete.
   */
  const fetchSetNewPassword = async (
    values: fetchLoginValueProps,
    setStatus: Dispatch<any>,
    setSubmitting: Dispatch<any>,
    setErrors: (errors: FormikErrors<fetchLoginValueProps>) => void
  ) => {
    setStatus('')
    try {
      await setNewPasswordMutation.mutateAsync({
        token: `${uid}/${token}`,
        password: values.new_password,
        password_confirm: values.confirmPassword,
      })

      setStatus('Your password has been successfully changed')
      setIsError(false)
      setTimeout(() => {
        navigate('/auth/login')
      }, 10000)
    } catch (e: any) {
      const errorData = e?.response?.data
      if (errorData) {
        setErrors(errorData)
      }
      setStatus(e?.message || 'An error occurred. Please try again.')
      setIsError(true)
      setSubmitting(false)
    }
  }

  /**
   * Initializes and configures the Formik form.
   *
   * @returns A Formik form instance.
   */
  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: (values: fetchLoginValueProps, {setStatus, setSubmitting, setErrors}) => {
      fetchSetNewPassword(values, setStatus, setSubmitting, setErrors)
    },
  })


  return (
    <div className='card'>
      <div className='px-md-5 py-5 card-body'>
        <div className='d-flex flex-column justify-content-center align-items-center mb-10 p-8 rounded'>
          <h1>
          {intl.formatMessage({id: 'New Password'})}
            </h1>
          {formik.status ? (
            <div
              className={`font-weight-bold alert-text ${
                isError ? 'alert-danger' : 'alert-success'
              } `}
            >
              {formik.status}
            </div>
          ) : (
            <span className='opacity-75'>
              {intl.formatMessage({id: 'Enter your new password'})}
               </span>
          )}
        </div>
        <form noValidate id='kt_login_password_reset_form' onSubmit={formik.handleSubmit}>
          <div data-mdb-input-init className='position-relative mb-4 form-outline'>
            <input
            dir="ltr"
              type={pass ? 'text' : 'password'}
              id='form3Example4'
              className={clsx(
                'form-control',
                {'is-invalid': formik.touched.new_password && formik.errors.new_password},
                {
                  'is-valid': formik.touched.new_password && !formik.errors.new_password,
                }
              )}
              placeholder=' '
              {...formik.getFieldProps('new_password')}
              onChange={(e) => {
                setNew_password(e.target.value)
                formik.setFieldValue('new_password', e.target.value)
              }}
            />
            <span
              className='position-absolute translate-middle btn btn-sm btn-icon me-n2'
              style={{top: 22, right: 13}}
              data-kt-password-meter-control='visibility'
              onClick={() => {
                setPass((perv) => !perv)
              }}
            >
              {!pass ? (
                <EyeOff size={24} className='text-white' style={{cursor: 'pointer'}} />
              ) : (
                <Eye size={24} style={{cursor: 'pointer'}} />
              )}
            </span>
            <div className='d-flex justify-content-between align-items-center py-1'>
              <label className='m-0 form-label' htmlFor='form3Example4'>
              {intl.formatMessage({id: 'New password'})}
                
              </label>
              {formik.touched.new_password && formik.errors.new_password && (
                <div className='text-center text-danger fv-plugins-message-container'>
                  <span role='alert'>{formik.errors.new_password}</span>
                </div>
              )}
            </div>
          </div>

          <div data-mdb-input-init className='position-relative mb-4 form-outline'>
            <input
            dir="ltr"
              type={confirmPass ? 'text' : 'password'}
              id='form3Example4'
              className={clsx(
                'form-control',
                {'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword},
                {
                  'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword,
                }
              )}
              placeholder=' '
              {...formik.getFieldProps('confirmPassword')}
            />
            <span
              className='position-absolute translate-middle btn btn-sm btn-icon me-n2'
              style={{top: 22, right: 13}}
              data-kt-password-meter-control='visibility'
              onClick={() => {
                setConfirmPass((perv) => !perv)
              }}
            >
              {!confirmPass ? (
                <EyeOff size={24} className='text-white' style={{cursor: 'pointer'}} />
              ) : (
                <Eye size={24} className='text-white' style={{cursor: 'pointer'}} />
              )}
            </span>
            <div className='d-flex justify-content-between align-items-center py-1'>
              <label className='m-0 form-label' htmlFor='form3Example4'>
              {intl.formatMessage({id: 'Confirm password'})}
                
              </label>
              {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                <div className='text-center text-danger fv-plugins-message-container'>
                  <span role='alert'>{formik.errors.confirmPassword}</span>
                </div>
              )}
            </div>
          </div>
          <button
            className='btn-block w-100 btn btn-lg btn-primary'
            type='submit'
            id='kt_sign_in_submit'
            disabled={setNewPasswordMutation.isPending || !formik.isValid}
            style={{opacity: 0.9}}
          >
            {!setNewPasswordMutation.isPending && <span className='indicator-label'>Set new password</span>}
            {setNewPasswordMutation.isPending && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </form>
        <hr className='w-100' />
        <div className='form-group d-flex flex-wrap justify-content-between align-items-center mt-5 px-8'>
          <Link to='/auth' id='kt_login_forgot' className='font-weight-bold'>
          {intl.formatMessage({id: 'Cancel'})}
            
          </Link>
        </div>
      </div>
    </div>
  )
}
